import React from 'react';
import { ShieldOff, Mail, Clock, FileCheck, AlertCircle, Trash2 } from 'lucide-react';

/**
 * Iter 83 — Play Store-mandated Account Deletion landing page.
 *
 * Available unauthenticated at: /account-deletion and /delete-account
 *
 * Google Play Store requires every app that allows account creation to also
 * publish a publicly accessible URL explaining how a user can request that
 * their account and associated data be deleted. This page satisfies that
 * requirement while honestly explaining Ellora EPC's statutory retention
 * obligations (Companies Act, IT Act, GST, EPF, labour laws — typically 8+
 * years for financial / project records).
 */
export default function AccountDeletion() {
  const lastUpdated = '19 May 2026';
  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-sm bg-rose-600 flex items-center justify-center">
              <ShieldOff size={18} className="text-white" />
            </div>
            <div>
              <div className="text-[10px] font-display font-bold uppercase tracking-[0.25em] text-[#3b4872]">Ellora EPC Pvt Ltd</div>
              <h1 className="font-display font-bold text-base text-slate-900">Account & Data Deletion Request</h1>
            </div>
          </div>
          <a href="/app" className="text-xs text-[#3b4872] font-display font-semibold hover:underline">Back to App →</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8" data-testid="account-deletion-page">
        <div className="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 mb-8 rounded-sm flex gap-3">
          <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <strong>Ellora EPC ERP is an internal-use application</strong> available only to authorised employees, contractors and vendors of Ellora EPC Pvt Ltd. Account deletion is processed through our HR &amp; Compliance team, not through self-service inside the app.
          </div>
        </div>

        <p className="text-xs text-slate-500 mb-6">Last updated: {lastUpdated}</p>

        <Section icon={Trash2} title="How to request account deletion">
          <p>To request deletion of your Ellora EPC ERP account and the personal data associated with it, send an email to:</p>
          <div className="bg-white border border-slate-200 rounded-sm p-4 my-4 text-sm">
            <div className="font-display font-bold text-slate-900 text-base">
              <a className="text-[#3b4872]" href="mailto:privacy@elloraepc.com?subject=Account%20Deletion%20Request%20%E2%80%94%20Ellora%20EPC%20ERP">privacy@elloraepc.com</a>
            </div>
            <div className="text-slate-600 mt-3 text-xs uppercase font-display font-bold tracking-widest">Required in the email body</div>
            <ol className="list-decimal pl-5 mt-2 text-sm text-slate-700 space-y-1">
              <li>Your full name (as it appears in your Ellora EPC ERP profile)</li>
              <li>Your employee ID (e.g. EEPCSTF1234)</li>
              <li>The email address linked to your account</li>
              <li>Your last working day with Ellora EPC (if applicable)</li>
              <li>A clear statement of your request — e.g. <em>"I request deletion of my Ellora EPC ERP account and the personal data associated with it."</em></li>
            </ol>
            <div className="text-xs text-slate-500 mt-3 italic">
              For your protection, requests sent from email addresses other than the one registered to your account may require additional identity verification before processing.
            </div>
          </div>
        </Section>

        <Section icon={Clock} title="Processing timeline">
          <p>Once we receive a valid request:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>Within 2 business days</strong> — we acknowledge receipt and verify your identity.</li>
            <li><strong>Within 30 calendar days</strong> — your account is disabled (you can no longer log in).</li>
            <li><strong>Within 90 calendar days</strong> — eligible personal data is purged or anonymised (see the "What is deleted" table below).</li>
          </ul>
        </Section>

        <Section icon={FileCheck} title="What is deleted, what is kept">
          <p>Indian statutory regulations (Companies Act 2013, Income Tax Act 1961, GST Act 2017, EPF &amp; MP Act, Contract Labour Act, Industrial Disputes Act, Information Technology Act 2000) require Ellora EPC to retain certain records for fixed periods even after an employment / contract relationship ends. The table below shows our policy:</p>
          <div className="overflow-x-auto my-4 border border-slate-200 rounded-sm">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">Data Type</th>
                  <th className="text-left px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">After Account Closure</th>
                  <th className="text-left px-3 py-2 font-display font-bold uppercase tracking-widest text-[10px]">Notes</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                <RowDel data="Login credentials, password hashes, active sessions, JWT tokens" />
                <RowDel data="Face-recognition descriptor vectors used for attendance verification" />
                <RowDel data="In-app notifications, task delegations, draft documents you authored" />
                <RowDel data="Profile photo, optional bio fields, language preferences" />
                <RowKeep data="Attendance logs, payroll records, payslips, IT declarations, reimbursement claims" period="8 years" reason="Income Tax Act §44AA, EPF compliance, audit trail" />
                <RowKeep data="Project records, BOQ entries, DPRs, SVRs, drawings, RA bills, POs that you authored or approved" period="8 years post project completion" reason="Companies Act 2013 §128, contract audit" />
                <RowKeep data="HSE incident reports involving you" period="As long as required by statute" reason="Factories Act, BOCW Act" />
                <RowKeep data="Audit logs of administrative actions taken by your account" period="7 years" reason="IT Act 2000 §43A, internal security audit" />
                <RowAnon data="Your name and email on historical records (anonymised to a tag like EX-EMPLOYEE-1234)" />
              </tbody>
            </table>
          </div>
        </Section>

        <Section icon={ShieldOff} title="If you are an employee currently working at Ellora EPC">
          <p>You cannot delete your active employee account while you are engaged with Ellora EPC, since your account is required to perform your job duties (attendance, time-sheets, expense claims, project work). Account deletion is processed as part of your offboarding when you leave the organisation.</p>
          <p>If you want to update specific personal details (phone, address, emergency contact, bank details, etc.) without deleting the account, you can:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Update them yourself via <strong>Profile → Edit Profile</strong> inside the app, or</li>
            <li>Email <a className="text-[#3b4872] font-semibold" href="mailto:hr@elloraepc.com">hr@elloraepc.com</a> for fields that aren't user-editable.</li>
          </ul>
        </Section>

        <Section icon={Mail} title="Need help or to escalate">
          <div className="bg-white border border-slate-200 rounded-sm p-4 my-3 text-sm">
            <div className="font-display font-bold text-slate-900">Grievance Officer · Data Privacy</div>
            <div className="text-slate-600 mt-1">Ellora EPC Pvt Ltd</div>
            <div className="text-slate-600 mt-2">Email: <a className="text-[#3b4872] font-semibold" href="mailto:privacy@elloraepc.com">privacy@elloraepc.com</a></div>
            <div className="text-slate-600">HR: <a className="text-[#3b4872] font-semibold" href="mailto:hr@elloraepc.com">hr@elloraepc.com</a></div>
            <div className="text-slate-600">Website: <a className="text-[#3b4872] font-semibold" href="https://elloraepc.in" target="_blank" rel="noreferrer">https://elloraepc.in</a></div>
          </div>
          <p className="text-xs text-slate-500">If your request is not addressed within 30 days, you may contact the Data Protection Board of India under the Digital Personal Data Protection Act, 2023.</p>
        </Section>

        <footer className="border-t border-slate-200 mt-10 pt-6 pb-12 text-xs text-slate-500">
          © {new Date().getFullYear()} Ellora EPC Pvt Ltd. All rights reserved. See also our <a className="text-[#3b4872]" href="/privacy">Privacy Policy</a>.
        </footer>
      </main>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="flex items-center gap-2 font-display font-bold text-base text-slate-900 mb-3 mt-6">
        <Icon size={15} className="text-[#3b4872]" />{title}
      </h2>
      <div className="text-sm text-slate-700 leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );
}

function RowDel({ data }) {
  return (
    <tr className="border-t border-slate-200">
      <td className="px-3 py-2">{data}</td>
      <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 bg-rose-100 text-rose-700 rounded text-[10px] font-display font-bold uppercase tracking-widest">Deleted</span></td>
      <td className="px-3 py-2 text-xs text-slate-500">Within 90 days of request</td>
    </tr>
  );
}
function RowKeep({ data, period, reason }) {
  return (
    <tr className="border-t border-slate-200">
      <td className="px-3 py-2">{data}</td>
      <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-display font-bold uppercase tracking-widest">Retained · {period}</span></td>
      <td className="px-3 py-2 text-xs text-slate-500">{reason}</td>
    </tr>
  );
}
function RowAnon({ data }) {
  return (
    <tr className="border-t border-slate-200">
      <td className="px-3 py-2">{data}</td>
      <td className="px-3 py-2"><span className="inline-block px-2 py-0.5 bg-slate-200 text-slate-700 rounded text-[10px] font-display font-bold uppercase tracking-widest">Anonymised</span></td>
      <td className="px-3 py-2 text-xs text-slate-500">Within 90 days</td>
    </tr>
  );
}
